import type { GridDiffStatus } from "./types";

/**
 * HTTP client for the CAD-compare service (routed via the BFF). Two endpoints:
 *
 *   POST /api/cad/compare-drawing           → { job_id }
 *   GET  /api/cad/drawing/{job_id}/status   → GridDiffStatus
 *
 * The POST body is JSON: `{ claude_model, file_a_item_id, file_b_item_id, prompt_extra }`.
 * The item ids are SharePoint drive item ids — the BFF fetches the PDF bytes
 * itself instead of the client uploading them.
 *
 * Apps pass in `apiUrl` and `getAuthToken` via `DocumentLibraryCompareConfig`.
 * `claudeApiKey` is hardcoded in the backend — restore the field here once
 * the BFF exposes it as a caller-supplied param.
 */

/** Azure Function host key appended to every BFF request as `?code=`. */
const BFF_FUNCTION_CODE =
  "OBIlx2pHPQ5xhGlQRqk81zq9V1St21hiRRWMMMXx5UYzAzFuVAN3-A==";

const bffUrl = (base: string, path: string) => {
  const url = new URL(`${base}${path}`);
  url.searchParams.set("code", BFF_FUNCTION_CODE);
  return url.toString();
};

export interface StartGridDiffParams {
  apiUrl: string;
  getAuthToken: () => Promise<string>;
  /** SharePoint item id of the first PDF. */
  fileAItemId: string;
  /** SharePoint item id of the second PDF. */
  fileBItemId: string;
  // /** Optional Claude API key — hardcoded in backend for now. */
  // claudeApiKey: string;
  /** Optional extra context appended to the built-in prompt. */
  promptExtra: string;
  /** Claude model id (e.g. "claude-sonnet-4-6"). */
  claudeModel: string;
  signal?: AbortSignal;
}

export const startGridDiff = async ({
  apiUrl,
  getAuthToken,
  fileAItemId,
  fileBItemId,
  // claudeApiKey,
  promptExtra,
  claudeModel,
  signal
}: StartGridDiffParams): Promise<{ job_id: string }> => {
  const token = await getAuthToken();

  const response = await fetch(bffUrl(apiUrl, "/api/cad/compare-drawing"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      claude_model: claudeModel,
      file_a_item_id: fileAItemId,
      file_b_item_id: fileBItemId,
      // claude_api_key: claudeApiKey,
      prompt_extra: promptExtra
    }),
    signal
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(body || `Grid diff start failed: ${response.status}`);
  }

  return response.json();
};

export const fetchGridDiffStatus = async (
  apiUrl: string,
  getAuthToken: () => Promise<string>,
  jobId: string,
  signal?: AbortSignal
): Promise<GridDiffStatus> => {
  const token = await getAuthToken();
  const response = await fetch(
    bffUrl(apiUrl, `/api/cad/drawing/${jobId}/status`),
    {
      headers: { Authorization: `Bearer ${token}` },
      signal
    }
  );
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(body || `Grid diff status failed: ${response.status}`);
  }
  return response.json();
};

export interface PollGridDiffOptions {
  apiUrl: string;
  getAuthToken: () => Promise<string>;
  jobId: string;
  /** Called on every successful poll. */
  onProgress?: (snapshot: GridDiffStatus) => void;
  signal?: AbortSignal;
  /** Milliseconds between polls. Default 1200. */
  intervalMs?: number;
  /** Consecutive transport failures tolerated before giving up. Default 10. */
  maxFailures?: number;
  /** Delay applied after a transport failure. Default 3000. */
  backoffMs?: number;
}

/**
 * Polls `status/{jobId}` until the job reaches a terminal state. Resolves
 * with the final snapshot, or rejects with the API's error message.
 *
 * Cancellation is cooperative — abort the signal and the loop exits at the
 * next iteration (in-flight `fetch` calls are also aborted).
 */
export const pollGridDiff = async ({
  apiUrl,
  getAuthToken,
  jobId,
  onProgress,
  signal,
  intervalMs = 1200,
  maxFailures = 10,
  backoffMs = 3000
}: PollGridDiffOptions): Promise<GridDiffStatus> => {
  let failures = 0;

  const wait = (ms: number) =>
    new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        signal?.removeEventListener("abort", onAbort);
        resolve();
      }, ms);
      const onAbort = () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      };
      if (signal?.aborted) {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      signal?.addEventListener("abort", onAbort, { once: true });
    });

  // Loop exits via `return` on terminal status or `throw` on abort/failure cap.

  while (true) {
    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    try {
      const snapshot = await fetchGridDiffStatus(
        apiUrl,
        getAuthToken,
        jobId,
        signal
      );
      failures = 0;
      onProgress?.(snapshot);

      if (snapshot.status === "done") return snapshot;
      if (snapshot.status === "error") {
        throw new Error(snapshot.message || "Grid diff pipeline failed.");
      }

      await wait(intervalMs);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      // Distinguish API-reported errors (which we rethrow) from transport
      // failures (which we back off and retry).
      if (err instanceof Error && err.message.includes("pipeline failed"))
        throw err;

      failures += 1;
      if (failures >= maxFailures) {
        throw new Error(
          `Lost connection to grid-diff service after ${maxFailures} retries. The analysis may still be running — try again later.`
        );
      }
      await wait(backoffMs);
    }
  }
};
